import { Box, VStack } from '@chakra-ui/layout';
import { Spacer } from '@chakra-ui/react';
import { TAG_VARIANTS } from 'library/atoms/Tag/Tag';
import TagStack from 'library/atoms/Tag/TagStack';
import { PRIORITY_OPTIONS } from 'library/common/constant';
import { FormSelect } from 'library/molecules';
import FormControlWrapper from 'library/molecules/FormControlWrapper';
import { FC, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { uniqueArrayObjects } from 'views/main/organization/analytics-v2/Filters/helper';
import {
  IncidentAnalyticsFilterResponseAssignee,
  IncidentAnalyticsFilterResponseAssigneeType,
} from 'views/main/organization/analytics-v3/api/types';
import {
  AssigneeDropdownOptions,
  Assignee,
  IncidentAnalyticsFilterForm,
} from 'views/main/organization/analytics-v3/hooks/types';
import { SquadFilterDropdown } from 'views/main/organization/owner-filters/Dropdown/AssigneeDropdown';
import { DropdownOption } from 'views/main/organization/owner-filters/hooks/useEntityOwnerFilter';
import { FilterFormProps } from './type';

const getDefaultAssignee = (): Assignee => ({
  meAndMySquads: false,
  users: [],
  squads: [],
  myUsers: [],
  mySquads: [],
});

const handleAssigneeChange = (
  value: any,
  currentAssignee: Assignee,
  setValue: (name: keyof IncidentAnalyticsFilterForm, value: any) => void,
  fieldName: 'assignee' | 'serviceOwner',
  meAndMySquads: IncidentAnalyticsFilterResponseAssignee['youAndYourSquads'],
) => {
  if (value?.type === 'select-all') {
    setValue(fieldName, {
      meAndMySquads: true,
      myUsers: meAndMySquads.map(el => ({
        ...el,
        assigneeType: IncidentAnalyticsFilterResponseAssigneeType.User,
        value: el.id,
      })),
      mySquads: meAndMySquads.map(el => ({
        ...el,
        assigneeType: IncidentAnalyticsFilterResponseAssigneeType.Squad,
        value: el.id,
      })),
      users: currentAssignee.users.filter(el => el.value !== currentAssignee.myUsers[0].value),
      squads: currentAssignee.squads.filter(
        el => ![...currentAssignee.mySquads].map(sq => sq.value).includes(el.value),
      ),
    });
  } else if (value?.type === 'user') {
    setValue(fieldName, {
      ...currentAssignee,
      users: uniqueArrayObjects(currentAssignee.users.length ? currentAssignee?.users : [], value),
    });
  } else if (value?.type === 'squad') {
    setValue(fieldName, {
      ...currentAssignee,
      squads: uniqueArrayObjects(
        currentAssignee.squads?.length ? currentAssignee.squads : [],
        value,
      ),
    });
  }
};

const handleAssigneeRemove = (
  type: string,
  id: string,
  currentAssignee: Assignee,
  setValue: (name: keyof IncidentAnalyticsFilterForm, value: any) => void,
  fieldName: 'assignee' | 'serviceOwner',
) => {
  if (id === 'meAndMySquads') {
    setValue(fieldName, {
      ...currentAssignee,
      meAndMySquads: false,
      myUsers: [],
      mySquads: [],
    });
  } else if (type === 'user') {
    setValue(fieldName, {
      ...currentAssignee,
      users: currentAssignee.users.filter((val: AssigneeDropdownOptions) => val.value != id),
    });
  } else if (type === 'squad') {
    setValue(fieldName, {
      ...currentAssignee,
      squads: currentAssignee?.squads?.filter((val: AssigneeDropdownOptions) => val.value != id),
    });
  }
};

const FilterForm: FC<FilterFormProps> = ({ tagMap, services, serviceOwnersMap, meAndMySquads }) => {
  const { setValue, watch } = useFormContext<IncidentAnalyticsFilterForm>();
  const selectedTagKey = watch('tagKey');
  const selectedTagOptions = tagMap.get(selectedTagKey?.value ?? '') || [];
  const selectedTagValue = watch('tagValue') || [];
  const selectedPriority = watch('priority') || [];
  const selectedServices = watch('service') || [];
  const assignee = watch('assignee') || getDefaultAssignee();
  const serviceOwner = watch('serviceOwner') || getDefaultAssignee();

  const selectedOwner = useMemo(
    () => [
      ...serviceOwner.users,
      ...serviceOwner.squads,
      ...serviceOwner.myUsers,
      ...serviceOwner.mySquads,
    ],
    [serviceOwner],
  );

  const availableServicesOptions = useMemo(() => {
    if (selectedOwner.length === 0) {
      return services.filter(service => !selectedServices.some(s => s.value === service.value));
    }

    const allowedServices = selectedOwner.reduce((acc: string[], owner) => {
      if (owner.value && serviceOwnersMap[owner.value]) {
        acc.push(...serviceOwnersMap[owner.value]);
      }
      return acc;
    }, []);

    return services
      .filter(service => allowedServices.includes(service.value))
      .filter(service => !selectedServices.some(s => s.value === service.value));
  }, [selectedOwner, serviceOwnersMap, services, selectedServices]);

  // filter out the selected tags

  const availableTagOptions = selectedTagOptions
    .map(value => ({ label: value, value }))
    .filter(value => !selectedTagValue.some(tag => tag.value === value.value));

  const availablePriorityOptions = PRIORITY_OPTIONS.filter(
    value => !selectedPriority.some(priority => priority.value === value.value),
  );

  const filterServiceOwnerOptionsWithoutServices = (options: DropdownOption[]) => {
    const newOptions = options
      .map(option => {
        return {
          ...option,
          options: option.options.filter(opt => serviceOwnersMap[opt.value]?.length > 0),
        };
      })
      .filter(option => option.options.length > 0);
    return newOptions;
  };

  return (
    <VStack>
      <Box width={'100%'} data-testid="filter-form">
        <SquadFilterDropdown
          label="Current Assignee"
          description="User or squad currently assigned to an incident."
          value={assignee}
          onClear={() => setValue('assignee', getDefaultAssignee())}
          type="assignee"
          placeholder="Select assignee"
          onChange={(value: any) =>
            handleAssigneeChange(value, assignee, setValue, 'assignee', meAndMySquads)
          }
          meAndMySqaudField={assignee}
          setterFunction={setValue}
          onRemove={(type: string, id: string) =>
            handleAssigneeRemove(type, id, assignee, setValue, 'assignee')
          }
        />
        <SquadFilterDropdown
          label="Service Owner"
          description={`Select a service owner to narrow down the list of services.
                        Only users and squads that own services are shown here.`}
          value={serviceOwner}
          onClear={() => setValue('serviceOwner', getDefaultAssignee())}
          type="assignee"
          placeholder="Select service owner"
          onChange={(value: any) => {
            setValue('service', []);
            handleAssigneeChange(value, serviceOwner, setValue, 'serviceOwner', meAndMySquads);
          }}
          meAndMySqaudField={serviceOwner}
          setterFunction={setValue}
          onRemove={(type: string, id: string) =>
            handleAssigneeRemove(type, id, serviceOwner, setValue, 'serviceOwner')
          }
          filterOptions={filterServiceOwnerOptionsWithoutServices}
        />

        <FormControlWrapper
          label="Services"
          value={selectedServices}
          onClear={() => setValue('service', [])}
        >
          <FormSelect
            options={availableServicesOptions}
            value={[]}
            label="Services"
            hideLabel
            onChange={(fieldName, value) => {
              if (Array.isArray(value)) {
                setValue('service', [...selectedServices, ...value]);
              }
            }}
            onBlur={() => {}}
            error={null}
            isMulti={true}
            fieldName="services"
            isClearable={false}
            data-testid="services-select"
          />
          <Spacer mb={2} />
          <TagStack
            tags={selectedServices.map(service => ({ label: service.label }))}
            variant={TAG_VARIANTS.PLAIN}
            onClose={index => {
              const newServices = [...selectedServices];
              newServices.splice(index, 1);
              setValue('service', newServices);
            }}
          />
        </FormControlWrapper>

        <FormControlWrapper
          label="Tags"
          value={selectedTagOptions}
          onClear={() => {
            setValue('tagKey', null);
            setValue('tagValue', []);
          }}
        >
          <FormSelect
            options={Array.from(tagMap.keys()).map(key => ({ label: key, value: key }))}
            value={selectedTagKey}
            label="Tags Key"
            hideLabel
            controlShouldRenderValue
            onChange={(fieldName, value) => {
              if (!Array.isArray(value)) {
                setValue('tagKey', {
                  label: value?.label ?? '',
                  value: value?.value ?? '',
                });
              }
              setValue('tagValue', []);
            }}
            onBlur={() => {}}
            error={null}
            isMulti={false}
            fieldName="tagsKey"
            isClearable={false}
            data-testid="tag-key-select"
          />
          <Spacer mb={2} />
          <FormSelect
            options={availableTagOptions}
            value={[]}
            label="Tags Value"
            hideLabel
            controlShouldRenderValue
            onChange={(fieldName, value) => {
              if (Array.isArray(value)) {
                setValue('tagValue', [...selectedTagValue, ...value]);
              }
            }}
            onBlur={() => {}}
            error={null}
            isMulti={true}
            fieldName="tagsValue"
            isClearable={false}
            isDisabled={!selectedTagKey?.value}
            data-testid="tag-value-select"
          />
          <Spacer mb={2} />
          <TagStack
            tags={selectedTagValue.map(value => ({
              label: `${selectedTagKey?.label ?? ''} : ${value.label}`,
            }))}
            variant={TAG_VARIANTS.PLAIN}
            onClose={index => {
              const newTagValue = [...selectedTagValue];
              newTagValue.splice(index, 1);
              setValue('tagValue', newTagValue);
            }}
          />
        </FormControlWrapper>

        <FormControlWrapper
          label="Priority"
          value={selectedPriority}
          onClear={() => setValue('priority', [])}
        >
          <FormSelect
            options={availablePriorityOptions}
            value={[]}
            label="Priority"
            hideLabel
            onChange={(fieldName, value) => {
              if (Array.isArray(value)) {
                setValue('priority', [...selectedPriority, ...value]);
              }
            }}
            onBlur={() => {}}
            error={null}
            isMulti={true}
            fieldName="priority"
            isClearable={false}
            variant="priority"
            data-testid="priority-select"
          />
          <Spacer mb={2} />
          <TagStack
            tags={selectedPriority.map(value => ({ label: value.label }))}
            onClose={index => {
              const newPriority = [...selectedPriority];
              newPriority.splice(index, 1);
              setValue('priority', newPriority);
            }}
            variant={TAG_VARIANTS.PLAIN}
          />
        </FormControlWrapper>
      </Box>
    </VStack>
  );
};

export default FilterForm;
